import { ApolloCache } from "@apollo/client"

import { exists } from "shared/lib/exists"
import { comparePosition } from "shared/lib/sorting/comparePosition"
import { findParent } from "shared/lib/treeUtils"
import {
  ProjectChild,
  ProjectParentTypename,
  ProjectTypename
} from "shared/types/Project"

import { readFavoritesFragment } from "entities/favorites/api/readFavoritesFragment"
import { FAVORITES_PROJECT_ID } from "entities/favorites/api/useFavoriteMutations"
import { filterSiblingsByType } from "entities/project-children/lib/filterSiblingsByType"
import { readProjectChildFragment } from "entities/project-children/lib/readProjectChildFragment"
import { readProjectParentFragment } from "entities/project-children/lib/readProjectParentFragment"

import { getProjectChildren } from "./getProjectChildren"

export type ChildPositionData = {
  prevPosition: number
  prevParentID: string | null
  prevParentType: ProjectParentTypename | null
  nextParentID: string | null
  nextParentType: ProjectParentTypename | null
  nextPosition: number
}

function getChildPosition({
  siblings,
  prevPosition,
  nextPosition
}: {
  siblings: ProjectChild[]
  prevPosition: number
  nextPosition: number
}): number {
  const sortedSiblings = [...siblings].sort(comparePosition)
  if (sortedSiblings.length === 0 || nextPosition === 0) {
    return 0
  }
  if (prevPosition !== -1) {
    sortedSiblings.splice(prevPosition, 1)
  }
  const positionAdjustment =
    prevPosition !== -1 && prevPosition < nextPosition ? -1 : 0

  const referenceElement =
    sortedSiblings[nextPosition - 1] ??
    sortedSiblings[sortedSiblings.length - 1]

  if (!referenceElement) {
    return 0
  }

  return referenceElement.position + 1 + positionAdjustment
}

export function getChildPositionData({
  projectID,
  parentID,
  childID,
  childType,
  displayPosition,
  cache
}: {
  projectID: string
  parentID?: string | null
  childID: string
  childType: "Floor" | "Room" | "ProductPosition"
  displayPosition?: number | null
  cache: ApolloCache<object>
}): ChildPositionData {
  const child = readProjectChildFragment(childID, cache)
  const children = getProjectChildren(projectID, ProjectTypename, cache)
  const prevParent = readProjectParentFragment(
    findParent(childID, children)?.id || projectID,
    cache
  )

  let nextParent: {
    id: string
    __typename?: ProjectParentTypename
    children?: ProjectChild[] | null
  } | null = readProjectParentFragment(
    parentID || prevParent?.id || projectID,
    cache
  )

  if (parentID === FAVORITES_PROJECT_ID) {
    nextParent = {
      ...readFavoritesFragment(cache),
      id: FAVORITES_PROJECT_ID,
      __typename: ProjectTypename
    }
  }

  if (!nextParent?.id || !nextParent.__typename) {
    return {
      prevPosition: child?.position ?? -1,
      prevParentID: prevParent?.id || null,
      prevParentType: prevParent?.__typename || null,
      nextParentID: nextParent?.id || null,
      nextParentType: nextParent?.__typename || null,
      nextPosition: -1
    }
  }

  const siblingsOfSameType = filterSiblingsByType({
    siblings: nextParent.children,
    childType
  })

  const prevPosition =
    prevParent?.id === nextParent.id
      ? siblingsOfSameType.findIndex(sibling => sibling.id === childID)
      : -1

  const nextPosition = getChildPosition({
    siblings: siblingsOfSameType,
    prevPosition,
    nextPosition: displayPosition ?? 0
  })

  return {
    prevPosition: child?.position ?? -1,
    prevParentID: prevParent?.id || null,
    prevParentType: prevParent?.__typename || null,
    nextParentID: nextParent.id,
    nextParentType: nextParent.__typename,
    nextPosition: exists(displayPosition)
      ? nextPosition
      : child?.position ??
        (siblingsOfSameType.length > 0
          ? siblingsOfSameType.reduce((m, s) => Math.max(m, s.position), 0) + 1
          : 0)
  }
}
